import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Moon, Sun, MapPin, Clock, AlertTriangle, Heart, Loader2 } from 'lucide-react';

const RamadanGuard = ({ open, onConfirm, onCancel, blockData }) => {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const info = blockData?.fasting_info || {};

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel?.()}>
      <DialogContent className="sm:max-w-md" data-testid="ramadan-guard-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Moon className="w-5 h-5 text-amber-400" />
            {fr ? 'Jeûne en cours' : 'Fasting Active'}
          </DialogTitle>
          <DialogDescription className="text-base">
            {blockData?.message}
          </DialogDescription>
        </DialogHeader>

        {/* Sun times card */}
        <div className="p-4 bg-amber-500/10 rounded-xl border border-amber-500/30 space-y-3" data-testid="ramadan-sun-times">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <Sun className="w-4 h-4 text-amber-400" />
              <span className="text-muted-foreground">{fr ? 'Fajr (début)' : 'Fajr (start)'}</span>
            </div>
            <span className="font-mono font-bold text-foreground">{info.fajr}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <Moon className="w-4 h-4 text-amber-400" />
              <span className="text-muted-foreground">{fr ? 'Maghrib (fin)' : 'Maghrib (end)'}</span>
            </div>
            <span className="font-mono font-bold text-foreground">{info.maghrib}</span>
          </div>
          {info.time_until_iftar && (
            <div className="flex items-center justify-between pt-2 border-t border-amber-500/20">
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-primary" />
                <span className="text-muted-foreground">{fr ? 'Iftar dans' : 'Iftar in'}</span>
              </div>
              <Badge variant="outline" className="border-primary text-primary font-mono">
                {info.time_until_iftar}
              </Badge>
            </div>
          )}
          {info.location && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
              <MapPin className="w-3 h-3" />
              {info.location}
            </div>
          )}
        </div>

        {/* Confirm prompt */}
        <div className="p-3 bg-secondary rounded-lg border border-border">
          <p className="text-sm text-foreground flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
            {blockData?.confirm_prompt}
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={onCancel}
            data-testid="ramadan-guard-cancel"
          >
            {fr ? 'Annuler' : 'Cancel'}
          </Button>
          <Button
            onClick={onConfirm}
            className="flex-1 bg-amber-500 hover:bg-amber-600 text-black"
            data-testid="ramadan-guard-confirm"
          >
            <Heart className="w-4 h-4 mr-2" />
            {fr ? 'Oui, exception santé' : 'Yes, health exception'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Hook to use in meal-related pages.
 * Returns { checkFasting, RamadanModal }
 */
export const useRamadanGuard = () => {
  const { user } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [blockData, setBlockData] = useState(null);
  const [resolveRef, setResolveRef] = useState(null);

  const checkFasting = useCallback(async () => {
    if (user?.cultural_mode !== 'ramadan') return true;

    // Quick check via API
    try {
      const res = await api.get('/meals/ramadan-status');
      if (res.data.active && res.data.is_fasting_hours) {
        return new Promise((resolve) => {
          setBlockData({
            ramadan_block: true,
            fasting_info: res.data,
            message: `Il fait jour (${res.data.now}) ! Le jeûne est en cours jusqu'au Maghrib (${res.data.maghrib}).`,
            confirm_prompt: "Es-tu en rupture de jeûne volontaire (santé, voyage, etc.) ?",
          });
          setResolveRef(() => resolve);
          setShowModal(true);
        });
      }
    } catch {
      // If check fails, allow through
    }
    return true;
  }, [user?.cultural_mode]);

  const handleConfirm = useCallback(() => {
    setShowModal(false);
    resolveRef?.(true);
    setResolveRef(null);
  }, [resolveRef]);

  const handleCancel = useCallback(() => {
    setShowModal(false);
    resolveRef?.(false);
    setResolveRef(null);
  }, [resolveRef]);

  const RamadanModal = (
    <RamadanGuard
      open={showModal}
      blockData={blockData}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );

  return { checkFasting, RamadanModal };
};

/**
 * Ramadan status banner for the dashboard/meals page
 */
export const RamadanBanner = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [status, setStatus] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const fr = language === 'fr';

  useEffect(() => {
    if (user?.cultural_mode === 'ramadan') {
      api.get('/meals/ramadan-status').then(res => setStatus(res.data)).catch(() => {});
    }
  }, [user?.cultural_mode]);

  const handleGeolocate = async () => {
    if (!navigator.geolocation) return;
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await api.post('/meals/ramadan-location', {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          });
          const res = await api.get('/meals/ramadan-status');
          setStatus(res.data);
        } catch {}
        setGeoLoading(false);
      },
      () => setGeoLoading(false),
      { timeout: 10000 }
    );
  };

  if (!status?.active) return null;

  return (
    <div
      className={`p-4 rounded-xl border mb-4 ${
        status.is_fasting_hours
          ? 'bg-amber-500/10 border-amber-500/30'
          : 'bg-emerald-500/10 border-emerald-500/30'
      }`}
      data-testid="ramadan-banner"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Moon className="w-5 h-5 text-amber-400" />
          <span className="font-heading uppercase tracking-tight text-sm">
            {status.is_fasting_hours
              ? (fr ? 'Jeûne en cours' : 'Fasting Active')
              : (fr ? 'Période de repas' : 'Eating Window')}
          </span>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span>Fajr {status.fajr}</span>
          <span>Maghrib {status.maghrib}</span>
          {status.time_until_iftar && (
            <Badge variant="outline" className="border-amber-500 text-amber-500 text-xs">
              Iftar {status.time_until_iftar}
            </Badge>
          )}
        </div>
      </div>
      {!status.coordinates?.lat && (
        <button
          onClick={handleGeolocate}
          disabled={geoLoading}
          className="mt-2 flex items-center gap-1.5 text-xs text-primary hover:underline"
          data-testid="ramadan-geolocate"
        >
          {geoLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <MapPin className="w-3 h-3" />}
          {fr ? 'Préciser ma position pour des heures exactes' : 'Set my location for exact times'}
        </button>
      )}
    </div>
  );
};

export default RamadanGuard;
